import {
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
  useDisclosure
} from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiCopy,
  FiHash,
  FiInfo,
  FiLoader,
  FiPackage,
  FiZap
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import {
  mockApi,
  type CreateLinkData,
  type Link as LinkType
} from "../api/mockApi";
import {
  AdvancedSettings,
  CampaignDetails,
  DestinationSection,
  DomainSelection,
  LinkPreview,
  PlatformSelection
} from "../components/CreateLink";

interface FormData {
  platform: string;
  redirectUrl: string;
  linkName: string;
  adSetName: string;
  adName: string;
  customId: string;
  metadata: string;
  forceExternalBrowser: boolean;
  domain: string;
  customQueryParams: string;
  destinationType: "single" | "ab-test" | "variant" | "custom";
  destinationPath: string;
  destinationPathA: string;
  destinationPathB: string;
  abTestSplit: number;
  destinationVariant: string;
  customRedirectUrl: string;
  abTestPaths: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
  quantity: number;
}

interface BulkCreateResult {
  links: LinkType[];
  errors: string[];
}

export default function BulkCreateLinks() {
  const [formData, setFormData] = useState<FormData>({
    platform: "",
    redirectUrl: "",
    linkName: "",
    adSetName: "",
    adName: "",
    customId: "",
    metadata: "",
    forceExternalBrowser: false,
    domain: "try.zown.ca",
    customQueryParams: "",
    destinationType: "single",
    destinationPath: "",
    destinationPathA: "",
    destinationPathB: "",
    abTestSplit: 50,
    destinationVariant: "",
    customRedirectUrl: "",
    abTestPaths: [
      { id: "1", type: "path", value: "", percentage: 50 },
      { id: "2", type: "path", value: "", percentage: 50 }
    ],
    abTestSchedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    },
    quantity: 5
  });
  
  const [quantityInput, setQuantityInput] = useState<string>("5");

  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [createdLinks, setCreatedLinks] = useState<LinkType[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Generate the full URL for a link
  const getLinkUrl = (id: string, domain: string = "try.zown.ca") => {
    return `https://${domain}/${id}`;
  };

  const bulkCreateMutation = useMutation({
    mutationFn: async (data: CreateLinkData & { quantity: number }) => {
      const links: LinkType[] = [];
      const errors: string[] = [];
      
      for (let i = 0; i < data.quantity; i++) {
        try {
          // Modify the data for each link to make them unique
          const linkData = {
            ...data,
            // Add number suffix to make each link unique
            linkName: data.linkName ? `${data.linkName} ${i + 1}` : undefined,
            adName: data.adName ? `${data.adName} ${i + 1}` : undefined
            // No customId for bulk created links - let the system generate them
          };
          
          const createdLink = await mockApi.createLink(linkData);
          links.push(createdLink);
        } catch (error) {
          errors.push(`Failed to create link ${i + 1}: ${error}`);
        }
      }
      
      return { links, errors };
    },
    onSuccess: (result: BulkCreateResult) => {
      queryClient.invalidateQueries({ queryKey: ["links"] });
      setCreatedLinks(result.links);
      onOpen();
      
      // Success notification is handled by the modal
    },
    onError: (error) => {
      console.error("Failed to create links:", error);
    }
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate custom URL if destination type is custom
    if (formData.destinationType === "custom") {
      if (!formData.customRedirectUrl) {
        setMetadataError("Custom redirect URL is required");
        return;
      }
      
      // Validate URL format
      try {
        const url = new URL(formData.customRedirectUrl);
        if (!['http:', 'https:'].includes(url.protocol)) {
          setMetadataError("Custom URL must start with http:// or https://");
          return;
        }
      } catch (error) {
        setMetadataError("Invalid custom redirect URL format");
        return;
      }
    }

    // Validate metadata if provided
    if (formData.metadata) {
      try {
        JSON.parse(formData.metadata);
      } catch (error) {
        setMetadataError("Invalid JSON format");
        return;
      }
    }

    const linkData: CreateLinkData & { quantity: number } = {
      platform: formData.platform,
      redirectUrl: "",
      linkName: formData.linkName || undefined,
      adSetName: formData.adSetName || undefined,
      adName: formData.adName || undefined,
      metadata: formData.metadata ? JSON.parse(formData.metadata) : undefined,
      forceExternalBrowser: formData.forceExternalBrowser,
      domain: formData.domain,
      customQueryParams: formData.customQueryParams || undefined,
      destinationType: formData.destinationType,
      destinationPath:
        formData.destinationType === "single"
          ? formData.destinationPath
          : undefined,
      destinationPathA:
        formData.destinationType === "ab-test"
          ? formData.destinationPathA
          : undefined,
      destinationPathB:
        formData.destinationType === "ab-test"
          ? formData.destinationPathB
          : undefined,
      abTestSplit:
        formData.destinationType === "ab-test"
          ? formData.abTestSplit
          : undefined,
      destinationVariant:
        formData.destinationType === "variant"
          ? formData.destinationVariant
          : undefined,
      abTestPaths:
        formData.destinationType === "ab-test"
          ? formData.abTestPaths
          : undefined,
      abTestSchedule:
        formData.destinationType === "ab-test"
          ? formData.abTestSchedule
          : undefined,
      quantity: formData.quantity
    };

    bulkCreateMutation.mutate(linkData);
  };

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCampaignChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleMetadataChange = (value: string, error: string | null) => {
    setFormData((prev) => ({ ...prev, metadata: value }));
    setMetadataError(error);
  };


  const handleCopyLink = (index: number) => {
    const link = createdLinks[index];
    const linkUrl = getLinkUrl(
      link.customId || link.id || "",
      link.domain || formData.domain
    );
    navigator.clipboard.writeText(linkUrl);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyAll = () => {
    const allLinks = createdLinks
      .map((link) =>
        getLinkUrl(
          link.customId || link.id || "",
          link.domain || formData.domain
        )
      )
      .join("\n");
    navigator.clipboard.writeText(allLinks);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const handleCreateMore = () => {
    // Reset form for new bulk creation
    setFormData({
      platform: "",
      redirectUrl: "",
      linkName: "",
      adSetName: "",
      adName: "",
      customId: "",
      metadata: "",
      forceExternalBrowser: false,
      domain: "try.zown.ca",
      customQueryParams: "",
      destinationType: "single",
      destinationPath: "",
      destinationPathA: "",
      destinationPathB: "",
      abTestSplit: 50,
      destinationVariant: "",
      customRedirectUrl: "",
      abTestPaths: [
        { id: "1", type: "path", value: "", percentage: 50 },
        { id: "2", type: "path", value: "", percentage: 50 }
      ],
      abTestSchedule: {
        enabled: false,
        startDate: "",
        endDate: ""
      },
      quantity: 5
    });
    setQuantityInput("5");
    setMetadataError(null);
    setCreatedLinks([]);
    setCopiedIndex(null);
    setCopiedAll(false);
    onClose();
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="7xl" py={8}>
        <form onSubmit={handleSubmit}>
          <VStack gap={8} align="stretch">
            {/* Header */}
            <HStack justify="space-between" align="center">
              <HStack gap={4}>
                <Link to="/links">
                  <Button
                    variant="ghost"
                    size="lg"
                    _hover={{ bg: "gray.100" }}
                  >
                    <Icon as={FiArrowLeft} />
                  </Button>
                </Link>
                <Box>
                  <HStack mb={1}>
                    <Icon as={FiPackage} color="blue.500" boxSize={5} />
                    <Text fontSize="2xl" fontWeight="bold">
                      Bulk Create Links
                    </Text>
                  </HStack>
                  <Text color="gray.600">
                    Create multiple tracking links at once
                  </Text>
                </Box>
              </HStack>
              <HStack gap={3}>
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  disabled={
                    bulkCreateMutation.isPending ||
                    !formData.platform ||
                    !!metadataError ||
                    formData.quantity < 1
                  }
                  width="180px"
                >
                  <HStack gap={2}>
                    {bulkCreateMutation.isPending ? (
                      <>
                        <Icon as={FiLoader} className="animate-spin" />
                        <Text>Creating...</Text>
                      </>
                    ) : (
                      <>
                        <Icon as={FiZap} />
                        <Text>Create {formData.quantity} Links</Text>
                      </>
                    )}
                  </HStack>
                </Button>
              </HStack>
            </HStack>

            {/* Quantity Selection */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiHash} color="purple.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Number of Links
                </Text>
              </HStack>
              <HStack gap={4}>
                <Input
                  type="number"
                  value={quantityInput}
                  onChange={(e) => {
                    setQuantityInput(e.target.value);
                    const num = parseInt(e.target.value);
                    if (!isNaN(num) && num >= 1 && num <= 100) {
                      handleFieldChange("quantity", num);
                    }
                  }}
                  onBlur={(e) => {
                    const num = parseInt(e.target.value);
                    if (isNaN(num) || num < 1) {
                      setQuantityInput("1");
                      handleFieldChange("quantity", 1);
                    } else if (num > 100) {
                      setQuantityInput("100");
                      handleFieldChange("quantity", 100);
                    } else {
                      setQuantityInput(num.toString());
                      handleFieldChange("quantity", num);
                    }
                  }}
                  min={1}
                  max={100}
                  size="lg"
                  width="200px"
                  placeholder="1-100"
                />
                <Text color="gray.600">
                  Each link will be created with a unique suffix (e.g., link-1, link-2, etc.)
                </Text>
              </HStack>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Maximum 100 links per batch
              </Text>
            </Box>

            {/* Main Content Grid */}
            <Grid templateColumns={{ base: "1fr", lg: "1fr 380px" }} gap={8}>
              {/* Left Column - Form */}
              <VStack gap={6} align="stretch">
                <PlatformSelection
                  platform={formData.platform}
                  onChange={(value) => handleFieldChange("platform", value)}
                  disabled={bulkCreateMutation.isPending}
                />

                <CampaignDetails
                  linkName={formData.linkName}
                  adSetName={formData.adSetName}
                  adName={formData.adName}
                  onChange={handleCampaignChange}
                  disabled={bulkCreateMutation.isPending}
                />

                <DomainSelection
                  domain={formData.domain}
                  onChange={(value) => handleFieldChange("domain", value)}
                  disabled={bulkCreateMutation.isPending}
                />

                <DestinationSection
                  domain={formData.domain}
                  destinationType={formData.destinationType}
                  destinationPath={formData.destinationPath}
                  destinationVariant={formData.destinationVariant}
                  customRedirectUrl={formData.customRedirectUrl}
                  abTestPaths={formData.abTestPaths}
                  abTestSchedule={formData.abTestSchedule}
                  onChange={handleFieldChange}
                  disabled={bulkCreateMutation.isPending}
                />


                <AdvancedSettings
                  metadata={formData.metadata}
                  metadataError={metadataError}
                  forceExternalBrowser={formData.forceExternalBrowser}
                  customQueryParams={formData.customQueryParams}
                  redirectUrl={formData.redirectUrl}
                  onMetadataChange={handleMetadataChange}
                  onForceExternalBrowserChange={(value) =>
                    handleFieldChange("forceExternalBrowser", value)
                  }
                  onCustomQueryParamsChange={(value) =>
                    handleFieldChange("customQueryParams", value)
                  }
                  disabled={bulkCreateMutation.isPending}
                />
              </VStack>

              {/* Right Column - Preview */}
              <Box position={{ lg: "sticky" }} top={8} height="fit-content">
                <LinkPreview
                  customId=""
                  domain={formData.domain}
                  platform={formData.platform}
                  linkName={formData.linkName}
                  adSetName={formData.adSetName}
                  adName={formData.adName}
                  destinationType={formData.destinationType === "custom" ? "single" : formData.destinationType}
                  destinationPath={formData.destinationType === "custom" ? formData.customRedirectUrl : formData.destinationPath}
                  destinationVariant={formData.destinationVariant}
                  abTestPaths={formData.abTestPaths}
                  abTestSchedule={formData.abTestSchedule}
                  forceExternalBrowser={formData.forceExternalBrowser}
                  customQueryParams={formData.customQueryParams}
                  generatedUrl={getLinkUrl(
                    "auto-generated",
                    formData.domain
                  )}
                />
                
                {/* Quantity Info Box */}
                <Box bg="blue.50" border="1px" borderColor="blue.200" rounded="lg" p={4} mt={4}>
                  <HStack>
                    <Icon as={FiInfo} color="blue.500" />
                    <Text fontSize="sm" color="blue.700">
                      You will create <strong>{formData.quantity}</strong> links with these settings.
                      Each link will have a unique identifier.
                    </Text>
                  </HStack>
                </Box>
              </Box>
            </Grid>
          </VStack>
        </form>
      </Container>

      {/* Success Modal with Links */}
      {isOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.600"
          zIndex={1000}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box
            bg="white"
            rounded="xl"
            shadow="2xl"
            maxW="4xl"
            w="90%"
            maxH="90vh"
            overflow="hidden"
            position="relative"
          >
            {/* Header */}
            <Box p={6} borderBottom="1px" borderColor="gray.200">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiCheckCircle} color="green.500" boxSize={6} />
                  <Text fontSize="xl" fontWeight="semibold">
                    Links Created Successfully!
                  </Text>
                </HStack>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate("/links")}
                >
                  ✕
                </Button>
              </HStack>
            </Box>

            {/* Body */}
            <Box p={6} maxH="60vh" overflowY="auto">
              <VStack align="stretch" gap={4}>
                <HStack justify="space-between">
                  <Text color="gray.600">
                    Created {createdLinks.length} tracking links
                  </Text>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={handleCopyAll}
                  >
                    <Icon as={copiedAll ? FiCheck : FiCopy} mr={2} />
                    {copiedAll ? "Copied All!" : "Copy All Links"}
                  </Button>
                </HStack>
                
                {/* Links List */}
                <Box border="1px" borderColor="gray.200" rounded="md" overflow="hidden">
                  <VStack align="stretch" gap={0}>
                    {createdLinks.map((link, index) => {
                      const linkUrl = getLinkUrl(
                        link.customId || link.id || "",
                        link.domain || formData.domain
                      );
                      return (
                        <Box key={link.id}>
                          {index > 0 && <Box borderBottom="1px" borderColor="gray.100" />}
                          <HStack p={3} justify="space-between">
                            <VStack align="start" gap={0} flex={1}>
                              <Text fontWeight="medium">
                                {link.linkName || "Unnamed Link"}
                              </Text>
                              {link.adName && (
                                <Text fontSize="xs" color="gray.500">
                                  {link.adName}
                                </Text>
                              )}
                              <Text fontSize="sm" fontFamily="mono" color="blue.600">
                                {linkUrl}
                              </Text>
                            </VStack>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleCopyLink(index)}
                            >
                              <Icon
                                as={copiedIndex === index ? FiCheck : FiCopy}
                                color={copiedIndex === index ? "green.500" : undefined}
                                mr={2}
                              />
                              {copiedIndex === index ? "Copied!" : "Copy"}
                            </Button>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>

                <Textarea
                  value={createdLinks
                    .map((link) =>
                      getLinkUrl(
                        link.customId || link.id || "",
                        link.domain || formData.domain
                      )
                    )
                    .join("\n")}
                  readOnly
                  fontFamily="mono"
                  fontSize="sm"
                  rows={Math.min(createdLinks.length, 5)}
                  resize="none"
                  bg="gray.50"
                />
              </VStack>
            </Box>

            {/* Footer */}
            <Box p={6} borderTop="1px" borderColor="gray.200">
              <HStack gap={3} justify="flex-end">
                <Button variant="outline" onClick={() => navigate("/links")}>
                  Go to Links
                </Button>
                <Button colorScheme="blue" onClick={handleCreateMore}>
                  Create More Links
                </Button>
              </HStack>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}